import React, { useState } from 'react';
import { Card, CardContent, Typography, Box, Button, Alert, Stack } from '@mui/material';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import { fetchApi } from '../hooks/useApi';

const FEATURES = [
  { icon: <SmartToyIcon color="primary" />, title: 'Unlimited AI Recommendations', desc: 'Personalized, tool-grounded wellness guidance on every calculation' },
  { icon: <QueryStatsIcon color="primary" />, title: 'Unlimited AI Dashboards', desc: 'AI-generated insights, trends, and focus areas from your history' },
];

/**
 * UpgradePrompt - premium upsell shown when the shared monthly free AI quota is
 * exhausted. Premium is NOT self-service: the button submits a request
 * (POST /user/{userId}/premium-request) that an admin must approve. Once a
 * request is pending (or the server already reports premiumStatus=pending), the
 * card shows a pending state instead of the request button.
 *
 * @param {Object} props
 * @param {boolean} props.visible
 * @param {string} props.userId
 * @param {string} props.premiumStatus - none|pending|approved|denied (from /tier)
 * @param {Function} props.onRequested - called after a successful request submit
 */
function UpgradePrompt({ visible, userId, premiumStatus, onRequested }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [justRequested, setJustRequested] = useState(false);

  if (!visible) {
    return null;
  }

  // Server status wins once it's terminal: justRequested is only an optimistic
  // bridge until the next /tier refresh, so a deny during the session clears the
  // pending view instead of sticking on it forever.
  const pending = premiumStatus === 'pending' || (justRequested && premiumStatus !== 'denied' && premiumStatus !== 'approved');

  const handleRequest = async () => {
    if (!userId) {
      setError('Could not determine your account. Please sign in again.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await fetchApi(`/user/${encodeURIComponent(userId)}/premium-request`, { method: 'POST' });
      setJustRequested(true);
      if (onRequested) onRequested(userId);
    } catch (err) {
      setError(err.message || 'Could not submit your request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card elevation={3} sx={{ mt: 3 }}>
      <Box sx={{ px: 3, py: 2, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', display: 'flex', alignItems: 'center', gap: 1 }}>
        <WorkspacePremiumIcon />
        <Typography variant="h3" component="h2" sx={{ color: '#fff' }}>Upgrade to Premium</Typography>
      </Box>
      <CardContent>
        {pending ? (
          <Stack spacing={1.5} alignItems="flex-start">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HourglassTopIcon color="warning" />
              <Typography>Your premium request is pending admin approval.</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              You'll get unlimited AI recommendations and dashboards once an admin approves it.
            </Typography>
          </Stack>
        ) : (
          <>
            <Typography sx={{ mb: 2 }}>
              You've used your free AI recommendations and dashboards for this month. Request
              Premium for unlimited AI-powered insights — an admin will review it.
            </Typography>
            <Stack spacing={2} sx={{ mb: 2 }}>
              {FEATURES.map((f) => (
                <Box key={f.title} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  {f.icon}
                  <Box>
                    <Typography variant="h3" sx={{ fontSize: '1rem' }}>{f.title}</Typography>
                    <Typography variant="body2" color="text.secondary">{f.desc}</Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Button fullWidth size="large" onClick={handleRequest} disabled={submitting}>
              {submitting ? 'Submitting…' : 'Request Premium'}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default UpgradePrompt;
